import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AdminSidebar } from '../../components/AdminSidebar';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { CheckCircle, LifeBuoy, Search, Send } from 'lucide-react';

import { API_URL } from '../../utils/api';
import { getErrorMessage } from '../../utils/errors';
import { formatPhone } from './AdminCalls';

const REFRESH_MS = 15000;

const FILTERS = [
  ['open', 'Support (open)'],
  ['unread', 'Waiting for us'],
  ['support', 'All support'],
  ['all', 'All texts'],
];

const when = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const today = new Date();
  return d.toDateString() === today.toDateString()
    ? d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
    : d.toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

/** An MMS attachment, fetched through the admin login (Twilio media needs our account). */
function Media({ url }) {
  const [src, setSrc] = useState(null);
  const [type, setType] = useState('');
  const [failed, setFailed] = useState(false);
  const open = async () => {
    try {
      const r = await axios.get(`${API_URL}/admin/sms/media`, { params: { url }, responseType: 'blob' });
      setType(r.data.type || '');
      setSrc(URL.createObjectURL(r.data));
    } catch {
      setFailed(true);
    }
  };
  useEffect(() => () => { if (src) URL.revokeObjectURL(src); }, [src]);
  if (failed) return <p className="text-xs opacity-60 mt-1">Picture not available</p>;
  if (!src) return <button type="button" className="text-xs underline mt-1" onClick={open}>Show picture</button>;
  if (type.startsWith('image/')) return <img src={src} alt="Customer picture" className="mt-1 max-h-64 rounded" />;
  return <a href={src} download className="text-xs underline mt-1 block">Download attachment</a>;
}

function Bubble({ m }) {
  const mine = m.direction === 'out';
  const fromTeam = mine && m.source === 'admin';
  const color = !mine
    ? 'bg-white border border-border'
    : fromTeam ? 'bg-gold/90 text-ink' : 'bg-ink/5 text-ink/80';
  return (
    <div className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm ${color}`}>
        <p className="whitespace-pre-wrap break-words" dir="auto">{m.body || (m.media_urls?.length ? '(picture)' : '')}</p>
        {m.direction === 'in' && (m.media_urls || []).map((u) => <Media key={u} url={u} />)}
        {m.direction === 'out' && m.media_urls?.length > 0 && m.body && <p className="text-xs opacity-60 mt-1">+ picture</p>}
        <p className="text-[10px] opacity-50 mt-1">
          {when(m.created_at)}
          {mine && (fromTeam ? ` · ${m.sender || 'team'}` : ' · bot')}
          {m.status === 'failed' && ' · not delivered'}
          {m.status === 'unknown' && ' · not confirmed'}
        </p>
      </div>
    </div>
  );
}

function Thread({ phone, onChanged }) {
  const [data, setData] = useState(null);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const sendId = useRef(null); // kept across retries of the same reply, so it's never sent twice
  const [onlySupport, setOnlySupport] = useState(false);
  const bottom = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/admin/sms/conversations/${encodeURIComponent(phone)}`);
      setData(r.data);
      // A new customer text showed up while this thread is open: it's been seen.
      if (r.data?.case?.unread) {
        // Only what's on screen counts as read: a text logged after this load keeps it waiting.
        const inbound = (r.data.messages || []).filter((m) => m.direction === 'in');
        const seenUntil = inbound.length ? inbound[inbound.length - 1].created_at : '';
        axios.post(`${API_URL}/admin/sms/conversations/${encodeURIComponent(phone)}/read`, { seen_until: seenUntil })
          .then(onChanged).catch(() => {});
      }
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to load conversation'));
    }
  }, [phone]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setData(null);
    load();
    const t = setInterval(load, REFRESH_MS);
    return () => clearInterval(t);
  }, [phone, load]); // eslint-disable-line react-hooks/exhaustive-deps

  const count = data?.messages?.length || 0;
  useEffect(() => { bottom.current?.scrollIntoView({ block: 'end' }); }, [count]);

  const send = async (e) => {
    e.preventDefault();
    if (!reply.trim()) return;
    setSending(true);
    try {
      if (!sendId.current) sendId.current = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const r = await axios.post(`${API_URL}/admin/sms/conversations/${encodeURIComponent(phone)}/reply`,
        { body: reply.trim(), client_id: sendId.current });
      setData(r.data);
      setReply('');
      sendId.current = null;
      onChanged();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Text not sent'));
      // An unconfirmed send: pressing Send again is a deliberate new send.
      if (err?.response?.status === 409 && /can't tell/.test(err.response.data?.detail || '')) { sendId.current = null; load(); }
    } finally {
      setSending(false);
    }
  };

  const closeCase = async () => {
    try {
      await axios.post(`${API_URL}/admin/sms/conversations/${encodeURIComponent(phone)}/close`);
      toast.success('Case closed; their texts go back to the bot');
      load();
      onChanged();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to close'));
    }
  };

  if (!data) return <div className="flex-1 flex items-center justify-center text-ink/40">Loading…</div>;

  const openCase = data.case;
  const optedOut = data.customer?.sms_opted_out;
  // "Support only" hides the bot conversation and shows texts since the first support case.
  const firstCase = data.cases?.length ? data.cases[data.cases.length - 1].created_at : null;
  const messages = onlySupport && firstCase
    ? data.messages.filter((m) => m.created_at >= firstCase && (m.direction === 'in' || m.source === 'admin'))
    : data.messages;

  return (
    <div className="flex-1 flex flex-col min-w-0">
      <div className="border-b border-border px-4 py-3 flex items-center justify-between gap-3 flex-wrap bg-white">
        <div>
          <p className="font-semibold">{formatPhone(phone)}</p>
          <p className="text-xs text-ink/50">
            {data.customer?.name || data.customer?.email || 'Customer'}
            {openCase ? ' · support case open' : data.cases?.length ? ' · support case closed' : ''}
            {optedOut ? ' · texted STOP' : ''}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {data.cases?.length > 0 && (
            <Button size="sm" variant="outline" onClick={() => setOnlySupport((v) => !v)}>
              {onlySupport ? 'Show everything' : 'Support only'}
            </Button>
          )}
          {openCase && (
            <Button size="sm" variant="outline" onClick={closeCase}><CheckCircle className="h-4 w-4 mr-1" />Close case</Button>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-2 bg-paper/50">
        {data.has_more && <p className="text-center text-xs text-ink/40">Older texts are not shown.</p>}
        {messages.length === 0 && <p className="text-center text-sm text-ink/40 py-10">No texts saved yet.</p>}
        {messages.map((m) => <Bubble key={m.id} m={m} />)}
        <div ref={bottom} />
      </div>

      <form onSubmit={send} className="border-t border-border p-3 bg-white flex gap-2 items-end">
        <Textarea
          dir="auto"
          rows={2}
          value={reply}
          maxLength={1500}
          disabled={optedOut || sending}
          onChange={(e) => { setReply(e.target.value); sendId.current = null; }}
          onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) send(e); }}
          placeholder={optedOut ? 'This customer texted STOP and can’t be texted.' : 'Text the customer… (Ctrl+Enter to send)'}
          className="flex-1"
        />
        <Button type="submit" disabled={optedOut || sending || !reply.trim()}>
          <Send className="h-4 w-4 mr-1" />Send
        </Button>
      </form>
      <p className="px-3 pb-2 text-[11px] text-ink/40 bg-white">
        After you reply, the customer’s texts come here instead of to the bot, until you close the case or they text MAIN.
      </p>
    </div>
  );
}

export default function AdminSmsInbox() {
  const [filter, setFilter] = useState('open');
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState(''); // the search the list (and its refresh) uses
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ open: 0, unread: 0 });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/admin/sms/conversations`, { params: { filter, search: appliedSearch || undefined } });
      setRows(r.data.conversations);
      setCounts({ open: r.data.open_cases, unread: r.data.unread_cases });
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to load inbox'));
    } finally {
      setLoading(false);
    }
  }, [filter, appliedSearch]);

  useEffect(() => {
    setLoading(true);
    load();
    const t = setInterval(load, REFRESH_MS);
    return () => clearInterval(t);
  }, [filter, appliedSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-screen bg-paper">
      <AdminSidebar />
      <div className="ml-64 p-8">
        <h1 className="font-serif text-3xl font-bold text-ink mb-2">SMS Inbox</h1>
        <p className="text-ink/60 mb-6">
          Every text with the SMS bot is saved here. Customers who text SUPPORT open a case you can answer.
          {counts.unread > 0 && <span className="ml-2 text-red-500 font-medium">{counts.unread} waiting for an answer</span>}
        </p>

        <div className="bg-white rounded-lg border border-border overflow-hidden flex h-[calc(100vh-12rem)] min-h-[480px]">
          <div className="w-80 shrink-0 border-r border-border flex flex-col">
            <div className="p-3 space-y-2 border-b border-border">
              <form className="relative" onSubmit={(e) => { e.preventDefault(); setAppliedSearch(search.trim()); }}>
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input placeholder="Number or words" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" />
              </form>
              <div className="flex flex-wrap gap-1">
                {FILTERS.map(([key, label]) => (
                  <Button key={key} size="sm" variant={filter === key ? 'default' : 'outline'} className="h-7 px-2 text-xs" onClick={() => setFilter(key)}>
                    {label}{key === 'open' && counts.open ? ` (${counts.open})` : ''}
                  </Button>
                ))}
              </div>
            </div>
            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <p className="p-4 text-sm text-ink/40">Loading…</p>
              ) : rows.length === 0 ? (
                <p className="p-4 text-sm text-ink/40">{filter === 'all' ? 'No texts yet.' : 'No support cases here.'}</p>
              ) : rows.map((r) => {
                const unread = r.case?.status === 'open' && r.case?.unread;
                return (
                  <button
                    key={r.phone}
                    type="button"
                    onClick={() => setSelected(r.phone)}
                    className={`w-full text-left px-3 py-2.5 border-b border-border/60 hover:bg-paper ${selected === r.phone ? 'bg-gold/10' : ''}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className={`text-sm ${unread ? 'font-semibold' : ''}`}>{formatPhone(r.phone)}</span>
                      <span className="text-[11px] text-ink/40 whitespace-nowrap">{when(r.last_message?.created_at || r.case?.last_message_at)}</span>
                    </div>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      {r.case?.status === 'open' && <LifeBuoy className={`h-3.5 w-3.5 shrink-0 ${unread ? 'text-red-500' : 'text-gold'}`} />}
                      <span className="text-xs text-ink/60 truncate" dir="auto">
                        {r.last_message ? `${r.last_message.direction === 'out' ? (r.last_message.source === 'admin' ? 'Team: ' : 'Bot: ') : ''}${r.last_message.body}` : r.case?.first_message}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
          {selected
            ? <Thread key={selected} phone={selected} onChanged={load} />
            : <div className="flex-1 flex items-center justify-center text-ink/40">Choose a conversation</div>}
        </div>
      </div>
    </div>
  );
}
